import { Button } from '@/modules/Button';
import { InnerButton } from '@/modules/InnerButton';
import { Separator } from '@/modules/Separator';
import { cn } from '@/shared/utils/cn';
import { SelectionAction } from '@/shared/types/table';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
interface TableSelectionBarProps {
  /** Número de elementos seleccionados */
  selectedCount: number;
  /** IDs de los elementos seleccionados */
  selectedIds: (string | number)[];
  /** Acciones disponibles para los elementos seleccionados */
  actions?: SelectionAction[];
  /** Handler para limpiar la selección */
  onClearSelection?: () => void;
  /** Texto personalizado para el contador (por defecto: "Seleccionados") */
  selectionText?: string;
  /** Clase CSS adicional */
  className?: string;
}

/**
 * TableSelectionBar - Barra de opciones para elementos seleccionados
 *
 * Según diseño de Figma:
 * - Fondo: bg-fill-brand (azul)
 * - Border radius: radius-surface-modal (16px)
 * - Padding: 16px horizontal, 8px vertical
 * - Gap: 16px entre elementos
 * - Texto blanco para el contador
 * - Divisor vertical entre contador y botones
 */
export const TableSelectionBar = ({
  selectedCount,
  selectedIds,
  actions = [],
  onClearSelection,
  selectionText = 'Seleccionados',
  className,
}: TableSelectionBarProps) => {
  if (selectedCount === 0) return null;

  const baseClasses = cn(
    'ui-inline-flex ui-items-center ui-gap-16',
    'ui-bg-fill-brand',
    'ui-px-16 ui-py-8',
    'ui-rounded-surface-modal',
    'ui-shadow-03',
    className,
  );

  return (
    <div className={baseClasses}>
      {/* Contador de seleccionados */}
      <span className="ui-whitespace-nowrap ui-text-body-sm-regular ui-font-body-sm-regular ui-text-on-color">
        ({selectedCount}) {selectionText}
      </span>

      {/* Divisor vertical */}
      {actions.length > 0 && (
        <Separator vertical className="ui-h-8 ui-border-on-color ui-opacity-30" />
      )}

      {/* Botones de acción */}
      <div className="ui-flex ui-items-center ui-gap-16">
        {actions.map((action, index) => (
          <Button
            key={`action-${index}-${action.label || action.icon}`}
            theme="solid"
            color={action.color || 'secondary'}
            size="xs"
            icon={action.label ? undefined : action.icon}
            leftSlot={action.label && action.icon ? action.icon : undefined}
            disabled={action.disabled}
            onClick={() => action.onClick(selectedIds)}
          >
            {action.label}
          </Button>
        ))}
      </div>

      {/* Botón para limpiar selección (opcional) */}
      {onClearSelection && (
        <>
          <Separator vertical className="ui-h-8 ui-border-on-color ui-opacity-30" />
          <InnerButton
            theme="solid"
            color="secondary"
            size="xs"
            pill="pill"
            iconProps={{ icon: faXmark }}
            onClick={onClearSelection}
          />
        </>
      )}
    </div>
  );
};
